import { useEffect, useState } from 'react';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { useAuthStore, selectUser } from '../stores/useAuthStore';
import { API_BASE_URL } from '../config/api';
import { getAuthToken } from '../utils/auth';
import { C2C_PLAN_PRESENTATION } from '../config/c2cPlanPresentation';

const faqs = [
  {
    q: "Can I cancel my subscription?",
    a: "Yes, you can cancel anytime. Access remains until the end of the paid period.",
  },
  {
    q: "What happens when I reach my interview limit?",
    a: "Limits and available interview modes depend on your selected plan from the catalog.",
  },
  {
    q: "How often does pricing update?",
    a: "This screen loads plans from the database on each visit, so prices and capabilities stay in sync with backend settings.",
  },
  {
    q: "Do I need to pick monthly or yearly billing?",
    a: "Pricing now comes directly from the live plans catalog. If yearly plans are added in the backend, they will appear here automatically.",
  },
];

const coachModes = [
  { name: "Friend", desc: "Warm & encouraging. Perfect for beginners.", plans: ["Free", "Sprint", "Pro"] },
  { name: "Guide", desc: "Balanced coaching with clear explanations.", plans: ["Sprint", "Pro"] },
  { name: "Challenger", desc: "Tough & critical. For confident candidates.", plans: ["Sprint", "Pro"] },
  { name: "Drill Sergeant", desc: "Brutal stress-test. No praise, only pressure.", plans: ["Pro"] },
];

export default function PricingScreen() {
  const authModal = useAuthModal();
  const authUser = useAuthStore(selectUser);

  const [selectedPlan, setSelectedPlan] = useState("free");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    let alive = true;
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/api/payments/plans`, { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (!alive) return;
        const merged = (Array.isArray(rows) ? rows : [])
          .slice()
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .map((p) => {
            const pres = C2C_PLAN_PRESENTATION[p.plan_code] || {};
            const isPaid = Number(p.price_usd) > 0;
            return {
              id: p.plan_code,
              planCode: p.plan_code,
              name: p.plan_name,
              priceLabel: `$${Number(p.price_usd).toFixed(0)}`,
              periodLabel: isPaid ? 'per month' : 'forever',
              isPaid,
              included: !isPaid,
              tagline: pres.tagline || '',
              description: pres.description || '',
              color: pres.accent || '#6b7280',
              cta: pres.cta || (isPaid ? 'Subscribe' : 'Start for free'),
              ctaStyle: isPaid ? 'solid' : 'outline',
              features: (pres.features || []).map((label) => ({ label, detail: '' })),
            };
          });
        setCatalog(merged);
      })
      .catch(() => { if (alive) setCatalog([]); });
    return () => { alive = false; };
  }, [authUser]);

  const visiblePlans = catalog;

  const activePlan = visiblePlans.find((p) => p.id === selectedPlan);

  useEffect(() => {
    if (activePlan) return;
    if (visiblePlans.length) {
      setSelectedPlan(visiblePlans[0].id);
    }
  }, [activePlan, visiblePlans]);

  const handleCheckout = async (plan) => {
    if (!plan?.isPaid || !plan?.planCode) {
      const token = getAuthToken();
      if (!token) {
        authModal.openAuth('profile', '/pricing');
      }
      return;
    }

    if (checkoutLoading) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      authModal.openAuth('profile', '/pricing');
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError('');

    try {
      const payload = {
        plan_code: plan.planCode,
        is_subscription: true,
        return_url: `${window.location.origin}/payments/result`,
        language: 'EN',
      };

      const response = await fetch(`${API_BASE_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setCheckoutError(data.detail || data.message || 'Failed to start checkout.');
        setCheckoutLoading(false);
        return;
      }
      if (data?.order_reference) {
        localStorage.setItem('lastPaymentOrderReference', data.order_reference);
      }
      // Stripe Checkout is a full-page redirect — no iframe, no in-page polling.
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('[Pricing] checkout error:', error);
      setCheckoutError('Unable to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#f8f9fa", minHeight: "100vh" }}>
      <SiteHeader authModal={authModal} />

      <style>{`
        @media (max-width: 768px) {
          .desktop-grid { display: none !important; }
          .mobile-tabs { display: flex !important; }
          .mobile-detail { display: block !important; }
          .coach-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (min-width: 769px) {
          .mobile-tabs { display: none !important; }
          .mobile-detail { display: none !important; }
        }
        .plan-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(0,0,0,0.12) !important; }
        .plan-card { transition: transform 0.2s, box-shadow 0.2s; }
        .cta-primary { background: #f97316 !important; }
        .cta-primary:hover { background: #ea6c0a !important; }
        .cta-solid:hover { opacity: 0.88; }
        .tab-btn { transition: all 0.15s; }
        .faq-item { border-bottom: 1px solid #e5e7eb; }
        .faq-item:last-child { border-bottom: none; }
      `}</style>

      <main style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 16px 80px" }}>
        {/* Hero */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <h1 style={{ fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 800, color: "#111", margin: "0 0 12px" }}>Choose a plan</h1>
          <p style={{ color: "#6b7280", fontSize: 16, margin: 0 }}>Practice interviews with AI. Cancel anytime.</p>
        </div>

        {checkoutError && (
          <div style={{ marginBottom: 24, padding: "16px 24px", borderRadius: 16, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: 14 }}>
            {checkoutError}
          </div>
        )}

        {/* Mobile: Plan Tabs */}
        <div className="mobile-tabs" style={{ display: "none", flexDirection: "column", gap: 0, marginBottom: 24, background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid #f0f0f0" }}>
            {visiblePlans.map((plan) => (
              <button
                key={plan.id}
                className="tab-btn"
                onClick={() => setSelectedPlan(plan.id)}
                style={{
                  padding: "14px 8px",
                  border: "none",
                  background: selectedPlan === plan.id ? "#fff7ed" : "#fff",
                  cursor: "pointer",
                  borderBottom: selectedPlan === plan.id ? "3px solid #f97316" : "3px solid transparent",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14, color: selectedPlan === plan.id ? "#f97316" : "#374151" }}>{plan.name}</span>
                <span style={{ fontSize: 13, color: selectedPlan === plan.id ? "#f97316" : "#6b7280", fontWeight: 600 }}>{plan.priceLabel}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Mobile: Selected Plan Detail */}
        <div className="mobile-detail" style={{ display: "none", marginBottom: 32 }}>
          {activePlan && (
            <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,0.08)", padding: "28px 24px", border: "1px solid #e5e7eb" }}>
              <div style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#6b7280" }}>{activePlan.tagline}</span>
              </div>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: "#111", margin: "0 0 4px" }}>
                {activePlan.priceLabel}
                <span style={{ fontSize: 15, fontWeight: 400, color: "#6b7280", marginLeft: 6 }}>{activePlan.periodLabel}</span>
              </h2>
              <p style={{ color: "#6b7280", fontSize: 14, margin: "8px 0 20px" }}>{activePlan.description}</p>
              <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>What's included</div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                  {activePlan.features.map((f, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 3 }}>
                        <circle cx="8" cy="8" r="8" fill="#f97316" fillOpacity="0.12"/>
                        <polyline points="4.5,8 7,10.5 11.5,5.5" stroke="#f97316" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{f.label}</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>{f.detail}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => handleCheckout(activePlan)}
                disabled={checkoutLoading || !activePlan.isPaid}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: 10,
                  border: activePlan.ctaStyle === "outline" ? "2px solid #e5e7eb" : "none",
                  background: activePlan.ctaStyle === "primary" ? "#f97316" : activePlan.ctaStyle === "outline" ? "#fff" : "#111",
                  color: activePlan.ctaStyle === "outline" ? "#374151" : "#fff",
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: (checkoutLoading || !activePlan.isPaid) ? "not-allowed" : "pointer",
                  opacity: (checkoutLoading || !activePlan.isPaid) ? 0.6 : 1,
                }}
              >
                {!activePlan.isPaid ? activePlan.cta : checkoutLoading ? "Starting checkout..." : activePlan.cta}
              </button>
            </div>
          )}
        </div>

        {/* Desktop: dynamic grid */}
        <div className="desktop-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${visiblePlans.length || 3}, 1fr)`, gap: 20, marginBottom: 56 }}>
          {visiblePlans.map((plan) => {
            const isCurrentCheckout = checkoutLoading;
            const isDisabled = isCurrentCheckout || !plan.isPaid;

            return (
              <div
                key={plan.id}
                className="plan-card"
                style={{
                  background: "#fff",
                  borderRadius: 20,
                  padding: "28px 22px",
                  boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                  border: "1px solid #e5e7eb",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 500 }}>{plan.tagline}</span>
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 800, color: "#111", margin: "0 0 2px" }}>
                  {plan.name}
                </h3>
                <div style={{ margin: "10px 0 6px" }}>
                  <span style={{ fontSize: 32, fontWeight: 800, color: "#111" }}>{plan.priceLabel}</span>
                  <span style={{ fontSize: 13, color: "#6b7280", marginLeft: 4 }}>{plan.periodLabel}</span>
                </div>
                <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 18px", lineHeight: 1.5 }}>{plan.description}</p>

                <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, marginBottom: 20, flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>What's included</div>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                    {plan.features.map((f, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                          <circle cx="8" cy="8" r="8" fill="#f97316" fillOpacity="0.12"/>
                          <polyline points="4.5,8 7,10.5 11.5,5.5" stroke="#f97316" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#111", lineHeight: 1.3 }}>{f.label}</div>
                          <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.3 }}>{f.detail}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handleCheckout(plan)}
                  disabled={isDisabled}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: 10,
                    border: plan.ctaStyle === "outline" ? "2px solid #e5e7eb" : "none",
                    background: plan.ctaStyle === "primary" ? "#f97316" : plan.ctaStyle === "outline" ? "#fff" : "#111",
                    color: plan.ctaStyle === "outline" ? "#374151" : "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    transition: "opacity 0.15s, background 0.15s",
                    opacity: isDisabled ? 0.6 : 1,
                  }}
                >
                  {plan.included ? "Included by default" : isCurrentCheckout ? "Starting checkout..." : plan.cta}
                </button>
              </div>
            );
          })}
        </div>

        {/* Coach Modes Section */}
        <div style={{ background: "#fff", borderRadius: 20, padding: "36px 32px", marginBottom: 40, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid #e5e7eb" }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111", margin: "0 0 6px" }}>AI Coach Modes</h2>
          <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 28px" }}>Choose how hard your AI coach pushes you — from warm support to brutal stress-testing.</p>
          <div className="coach-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            {coachModes.map((mode) => (
              <div key={mode.name} style={{ background: "#f9fafb", borderRadius: 14, padding: "20px 16px", border: "1px solid #f0f0f0" }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#111", marginBottom: 6 }}>{mode.name}</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12, lineHeight: 1.4 }}>{mode.desc}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {mode.plans.map((p) => (
                    <span key={p} style={{ fontSize: 11, background: "#fff7ed", color: "#f97316", border: "1px solid #fed7aa", borderRadius: 20, padding: "2px 8px", fontWeight: 600 }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Methods */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8 }}>Payment methods</p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
            {["Visa", "Mastercard", "PayPal", "Crypto"].map((m) => (
              <span key={m} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 20px", fontSize: 14, color: "#374151", fontWeight: 500 }}>{m}</span>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: "#111", textAlign: "center", marginBottom: 28 }}>Frequently asked questions</h2>
          <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #e5e7eb" }}>
            {faqs.map((faq, i) => (
              <div key={i} className="faq-item">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: "100%", padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", cursor: "pointer", textAlign: "left", gap: 16 }}
                >
                  <span style={{ fontWeight: 600, fontSize: 15, color: "#111" }}>{faq.q}</span>
                  <span style={{ color: "#f97316", fontSize: 20, lineHeight: 1, flexShrink: 0, transform: openFaq === i ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}>+</span>
                </button>
                {openFaq === i && (
                  <div style={{ padding: "0 24px 18px", color: "#6b7280", fontSize: 14, lineHeight: 1.6 }}>{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
